/**
 * Timeline Utility Functions
 *
 * Pure functions for timeline calculations, snap detection, time formatting, etc.
 */

import type { TimelineAudioClip, TimelineClip, TimelineRenderConfig, TimelineState } from './types'

const AUDIO_GAP_EPSILON_S = 0.001

export interface TimelineAudioInterval {
  startS: number
  endS: number
}

export interface BuildSelectiveAudioFillClipsInput {
  sourceNodeId: string
  sourcePath: string
  originalDuration: number
  role: TimelineAudioClip['role']
  volume: number
  muted?: boolean
  startOffset: number
  trackIndex: number
  timelineEndS: number
  existingAudioClips: TimelineAudioClip[]
  idFactory?: () => string
}

function makeTimelineAudioClipId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `audio-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function mergeIntervals(intervals: TimelineAudioInterval[]): TimelineAudioInterval[] {
  const sorted = intervals
    .filter((interval) => interval.endS - interval.startS > AUDIO_GAP_EPSILON_S)
    .sort((a, b) => a.startS - b.startS || a.endS - b.endS)

  const merged: TimelineAudioInterval[] = []
  for (const interval of sorted) {
    const previous = merged[merged.length - 1]
    if (!previous || interval.startS > previous.endS + AUDIO_GAP_EPSILON_S) {
      merged.push({ ...interval })
      continue
    }
    previous.endS = Math.max(previous.endS, interval.endS)
  }
  return merged
}

export function getAudioCoverageIntervals(
  audioClips: TimelineAudioClip[],
  rangeStartS: number,
  rangeEndS: number,
): TimelineAudioInterval[] {
  if (rangeEndS <= rangeStartS) return []
  return mergeIntervals(
    audioClips
      .filter((clip) => !clip.muted)
      .map((clip) => ({
        startS: Math.max(rangeStartS, clip.startOffset),
        endS: Math.min(rangeEndS, getTimelineClipEnd(clip)),
      }))
  )
}

export function getAudioGapIntervals(
  coverage: TimelineAudioInterval[],
  rangeStartS: number,
  rangeEndS: number,
): TimelineAudioInterval[] {
  if (rangeEndS <= rangeStartS) return []

  const gaps: TimelineAudioInterval[] = []
  let cursor = rangeStartS
  for (const interval of mergeIntervals(coverage)) {
    if (interval.startS > cursor + AUDIO_GAP_EPSILON_S) {
      gaps.push({ startS: cursor, endS: interval.startS })
    }
    cursor = Math.max(cursor, interval.endS)
  }
  if (rangeEndS > cursor + AUDIO_GAP_EPSILON_S) {
    gaps.push({ startS: cursor, endS: rangeEndS })
  }
  return gaps
}

/**
 * Build audio clip fragments for a background/master track.
 *
 * If the target timeline already has unmuted audio coverage, the new source is
 * split into the uncovered regions so it does not stack over scene-level music
 * or speech. When there is no existing audio coverage, this preserves the old
 * behavior by returning one continuous clip capped to the video timeline end.
 */
export function buildSelectiveAudioFillClips({
  sourceNodeId,
  sourcePath,
  originalDuration,
  role,
  volume,
  muted = false,
  startOffset,
  trackIndex,
  timelineEndS,
  existingAudioClips,
  idFactory = makeTimelineAudioClipId,
}: BuildSelectiveAudioFillClipsInput): TimelineAudioClip[] {
  const sourceDuration = Math.max(0, originalDuration)
  const rangeStartS = Math.max(0, startOffset)
  const rangeEndS = Number.isFinite(timelineEndS)
    ? Math.max(rangeStartS, timelineEndS)
    : rangeStartS + sourceDuration

  if (sourceDuration <= AUDIO_GAP_EPSILON_S || rangeEndS <= rangeStartS + AUDIO_GAP_EPSILON_S) {
    return []
  }

  const coverage = getAudioCoverageIntervals(existingAudioClips, rangeStartS, rangeEndS)
  const gaps = getAudioGapIntervals(coverage, rangeStartS, rangeEndS)

  const clips: TimelineAudioClip[] = []
  let consumedSourceS = 0
  for (const gap of gaps) {
    if (consumedSourceS >= sourceDuration - AUDIO_GAP_EPSILON_S) break

    const requestedDuration = gap.endS - gap.startS
    const segmentDuration = Math.min(requestedDuration, sourceDuration - consumedSourceS)
    if (segmentDuration <= AUDIO_GAP_EPSILON_S) continue

    const sourceInS = consumedSourceS
    const sourceOutS = sourceInS + segmentDuration
    clips.push({
      id: idFactory(),
      sourceNodeId,
      sourcePath,
      originalDuration: sourceDuration,
      trimStart: sourceInS,
      trimEnd: Math.max(0, sourceDuration - sourceOutS),
      effectiveDuration: segmentDuration,
      volume,
      muted,
      startOffset: gap.startS,
      role,
      speed: 1.0,
      trackIndex: Math.max(0, trackIndex),
    })
    consumedSourceS = sourceOutS
  }

  return clips
}

export function refreshTimelineAudioClipSourceDuration(
  clip: TimelineAudioClip,
  realDuration: number,
): TimelineAudioClip {
  const nextOriginalDuration = Number.isFinite(realDuration) ? Math.max(0, realDuration) : clip.originalDuration
  if (Math.abs(nextOriginalDuration - clip.originalDuration) <= AUDIO_GAP_EPSILON_S) {
    return clip
  }

  const speed = Number.isFinite(clip.speed) && clip.speed > AUDIO_GAP_EPSILON_S ? clip.speed : 1.0
  const oldSourceSpanS = Math.max(0, clip.effectiveDuration * speed)
  const wasFullSource =
    clip.trimStart <= AUDIO_GAP_EPSILON_S &&
    clip.trimEnd <= AUDIO_GAP_EPSILON_S &&
    Math.abs(oldSourceSpanS - clip.originalDuration) <= AUDIO_GAP_EPSILON_S

  if (wasFullSource) {
    return {
      ...clip,
      originalDuration: nextOriginalDuration,
      trimEnd: 0,
      effectiveDuration: nextOriginalDuration / speed,
    }
  }

  const trimStart = Math.min(Math.max(0, clip.trimStart), nextOriginalDuration)
  const sourceOutS = Math.min(nextOriginalDuration, trimStart + oldSourceSpanS)
  return {
    ...clip,
    originalDuration: nextOriginalDuration,
    trimStart,
    trimEnd: Math.max(0, nextOriginalDuration - sourceOutS),
    effectiveDuration: Math.max(0, sourceOutS - trimStart) / speed,
  }
}

/**
 * Format time in HH:MM:SS:FF format
 * @param seconds Time in seconds
 * @param fps Frames per second (default 30)
 */
export function formatTime(seconds: number, fps: number = 30): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const frames = Math.floor((seconds % 1) * fps)

  const hh = hours.toString().padStart(2, '0')
  const mm = minutes.toString().padStart(2, '0')
  const ss = secs.toString().padStart(2, '0')
  const ff = frames.toString().padStart(2, '0')

  if (hours > 0) {
    return `${hh}:${mm}:${ss}:${ff}`
  }
  return `${mm}:${ss}:${ff}`
}

/**
 * Format time in simple MM:SS format
 */
export function formatTimeSimple(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const mm = minutes.toString().padStart(2, '0')
  const ss = secs.toString().padStart(2, '0')
  return `${mm}:${ss}`
}

/**
 * Convert pixels to seconds based on zoom level
 */
export function pixelsToSeconds(pixels: number, zoomLevel: number): number {
  return pixels / zoomLevel
}

/**
 * Convert seconds to pixels based on zoom level
 */
export function secondsToPixels(seconds: number, zoomLevel: number): number {
  return seconds * zoomLevel
}

export function getTimelineClipEnd(clip: TimelineClip | TimelineAudioClip): number {
  return Math.max(0, clip.startOffset) + Math.max(0, clip.effectiveDuration)
}

export function computeTimelineDuration(
  clips: TimelineClip[],
  audioClips: TimelineAudioClip[] = []
): number {
  const videoEnd = clips.reduce((max, clip) => Math.max(max, getTimelineClipEnd(clip)), 0)
  const audioEnd = audioClips.reduce((max, clip) => Math.max(max, getTimelineClipEnd(clip)), 0)
  return Math.max(videoEnd, audioEnd)
}

/**
 * Calculate clip position (in pixels) on timeline
 */
export function getClipPosition(clip: TimelineClip, clips: TimelineClip[], zoomLevel: number): number {
  const startOffset = typeof clip.startOffset === 'number'
    ? clip.startOffset
    : clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)
  return secondsToPixels(startOffset, zoomLevel)
}

/**
 * Calculate clip width (in pixels) on timeline
 */
export function getClipWidth(clip: TimelineClip, zoomLevel: number): number {
  return secondsToPixels(clip.effectiveDuration, zoomLevel)
}

/**
 * Find clip at a given time position
 */
export function getClipAtTime(clips: TimelineClip[], time: number): TimelineClip | null {
  return [...clips]
    .sort((a, b) => a.trackIndex - b.trackIndex || a.order - b.order)
    .find((clip) => time >= clip.startOffset && time < getTimelineClipEnd(clip)) ?? null
}

/**
 * Get time offset within a clip (relative to clip's trimmed start)
 */
export function getTimeOffsetInClip(clip: TimelineClip, clips: TimelineClip[], globalTime: number): number {
  const startOffset = typeof clip.startOffset === 'number'
    ? clip.startOffset
    : clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)
  return Math.max(0, Math.min(globalTime - startOffset, clip.effectiveDuration))
}

/**
 * Snap detection threshold (in pixels). Used by clip-move drag and clip-edge drag.
 */
export const SNAP_THRESHOLD = 8

/**
 * Find snap position for a clip being moved (whole-clip drag).
 * Returns the snap position in pixels if within threshold, null otherwise.
 * Always snaps to timeline start (0) and to other clips' start/end pixels.
 */
export function findSnapPosition(
  _draggedClip: TimelineClip,
  otherClips: TimelineClip[],
  currentPosition: number,
  zoomLevel: number
): number | null {
  const snapTargets: number[] = [0]

  otherClips.forEach((clip) => {
    const clipStart = getClipPosition(clip, otherClips, zoomLevel)
    const clipEnd = clipStart + getClipWidth(clip, zoomLevel)
    snapTargets.push(clipStart, clipEnd)
  })

  let closestSnap: number | null = null
  let minDistance = SNAP_THRESHOLD

  snapTargets.forEach((target) => {
    const distance = Math.abs(currentPosition - target)
    if (distance < minDistance) {
      minDistance = distance
      closestSnap = target
    }
  })

  return closestSnap
}

/**
 * Edge-drag snap (Issue 2). Snap targets:
 *  - The red playhead cursor pixel (if provided)
 *  - The start and end pixels of OTHER clips on the SAME track as the dragged clip
 *
 * Returns the snap pixel within SNAP_THRESHOLD of the dragged edge, or null.
 */
export function findEdgeSnapPosition(
  edgePixel: number,
  sameTrackOtherClips: TimelineClip[],
  zoomLevel: number,
  playheadPixel: number | null
): { snapPx: number; target: 'playhead' | 'clip-edge' } | null {
  const candidates: { px: number; target: 'playhead' | 'clip-edge' }[] = []
  if (playheadPixel !== null && Number.isFinite(playheadPixel)) {
    candidates.push({ px: playheadPixel, target: 'playhead' })
  }
  sameTrackOtherClips.forEach((c) => {
    const start = getClipPosition(c, sameTrackOtherClips, zoomLevel)
    const end = start + getClipWidth(c, zoomLevel)
    candidates.push({ px: start, target: 'clip-edge' })
    candidates.push({ px: end, target: 'clip-edge' })
  })

  let best: { snapPx: number; target: 'playhead' | 'clip-edge' } | null = null
  let minDistance = SNAP_THRESHOLD
  candidates.forEach((c) => {
    const distance = Math.abs(edgePixel - c.px)
    if (distance < minDistance) {
      minDistance = distance
      best = { snapPx: c.px, target: c.target }
    }
  })
  return best
}

/**
 * Calculate new clip order based on drag position
 */
export function calculateNewOrder(
  draggedClipId: string,
  newPosition: number,
  clips: TimelineClip[],
  zoomLevel: number
): number {
  const draggedClip = clips.find((c) => c.id === draggedClipId)
  if (!draggedClip) return 0

  const otherClips = clips.filter((c) => c.id !== draggedClipId)

  // Find which position this clip should be at based on pixel position
  let cumulativeTime = 0
  let newOrder = 0

  for (let i = 0; i < otherClips.length; i++) {
    const clipPosition = secondsToPixels(cumulativeTime, zoomLevel)
    if (newPosition < clipPosition) {
      return newOrder
    }
    cumulativeTime += otherClips[i].effectiveDuration
    newOrder++
  }

  return newOrder
}

/**
 * Generate timeline ruler tick marks
 * Returns array of { position: pixels, label: string, isMajor: boolean }
 */
export interface RulerTick {
  position: number
  label: string
  isMajor: boolean
}

export function generateRulerTicks(
  totalDuration: number,
  zoomLevel: number,
  viewportWidth: number
): RulerTick[] {
  const ticks: RulerTick[] = []

  // Determine tick interval based on zoom level
  let majorInterval: number
  let minorInterval: number

  if (zoomLevel >= 100) {
    // Zoomed in: major every 1s, minor every 0.2s
    majorInterval = 1
    minorInterval = 0.2
  } else if (zoomLevel >= 50) {
    // Medium: major every 3s, minor every 1s
    majorInterval = 3
    minorInterval = 1
  } else {
    // Zoomed out: major every 10s, minor every 2s
    majorInterval = 10
    minorInterval = 2
  }

  const maxTime = Math.max(totalDuration, pixelsToSeconds(viewportWidth, zoomLevel))

  // Generate minor ticks
  for (let t = 0; t <= maxTime; t += minorInterval) {
    const isMajor = t % majorInterval === 0
    ticks.push({
      position: secondsToPixels(t, zoomLevel),
      label: isMajor ? formatTimeSimple(t) : '',
      isMajor,
    })
  }

  return ticks
}

/**
 * Clamp value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

/**
 * Calculate clip time boundaries (start and end in global timeline)
 */
export function getClipTimeBoundaries(
  clip: TimelineClip,
  clips: TimelineClip[]
): { startTime: number; endTime: number } {
  const startTime = typeof clip.startOffset === 'number'
    ? clip.startOffset
    : clips
        .filter((c) => c.order < clip.order)
        .reduce((sum, c) => sum + c.effectiveDuration, 0)
  return { startTime, endTime: startTime + clip.effectiveDuration }
}

type TrackPositionedClip = TimelineClip | TimelineAudioClip

const TIMELINE_OVERLAP_EPSILON = 1e-6

function clipSortOrder(clip: TrackPositionedClip): number {
  return 'order' in clip && typeof clip.order === 'number' ? clip.order : Number.MAX_SAFE_INTEGER
}

/**
 * Enforce the timeline invariant that clips in the same track do not overlap.
 *
 * If an edited clip is provided and it ties another clip's start, the edited clip
 * keeps that requested start and the colliding clip is shifted right. If the
 * requested start falls inside an earlier clip, the edited clip is clamped to the
 * earlier clip's end and later collisions cascade right.
 */
export function preventSameTrackOverlaps(
  clips: TimelineClip[],
  editedClipId?: string,
  options?: { allowNegativeForEditedClip?: boolean }
): TimelineClip[]
export function preventSameTrackOverlaps(
  clips: TimelineAudioClip[],
  editedClipId?: string,
  options?: { allowNegativeForEditedClip?: boolean }
): TimelineAudioClip[]
export function preventSameTrackOverlaps(
  clips: TrackPositionedClip[],
  editedClipId?: string,
  options?: { allowNegativeForEditedClip?: boolean }
): TrackPositionedClip[] {
  const allowNegativeForEdited = options?.allowNegativeForEditedClip === true
  const byTrack = new Map<number, Array<{ clip: TrackPositionedClip; index: number }>>()

  clips.forEach((clip, index) => {
    const trackIndex = typeof clip.trackIndex === 'number' ? Math.max(0, clip.trackIndex) : 0
    const trackClips = byTrack.get(trackIndex) ?? []
    trackClips.push({ clip: { ...clip, trackIndex }, index })
    byTrack.set(trackIndex, trackClips)
  })

  const normalized = [...clips]

  for (const trackClips of byTrack.values()) {
    const sorted = [...trackClips].sort((a, b) => {
      // Sort uses raw startOffset (may be negative) so edited clip stays leftmost
      const startDelta = a.clip.startOffset - b.clip.startOffset
      if (Math.abs(startDelta) > TIMELINE_OVERLAP_EPSILON) return startDelta
      if (a.clip.id === editedClipId && b.clip.id !== editedClipId) return -1
      if (b.clip.id === editedClipId && a.clip.id !== editedClipId) return 1
      return clipSortOrder(a.clip) - clipSortOrder(b.clip) || a.index - b.index
    })

    let nextAvailableStart = -Infinity
    let initialized = false
    for (const { clip, index } of sorted) {
      const isEdited = clip.id === editedClipId
      const allowNegativeHere = allowNegativeForEdited && isEdited
      const baseStart = allowNegativeHere ? clip.startOffset : Math.max(0, clip.startOffset)
      const requestedStart = baseStart
      const startOffset = initialized && requestedStart < nextAvailableStart - TIMELINE_OVERLAP_EPSILON
        ? nextAvailableStart
        : requestedStart
      const effectiveDuration = Math.max(0, clip.effectiveDuration)

      normalized[index] =
        startOffset === clip.startOffset &&
        effectiveDuration === clip.effectiveDuration &&
        clip.trackIndex === normalized[index].trackIndex
          ? clip
          : { ...clip, startOffset, effectiveDuration }
      nextAvailableStart = startOffset + effectiveDuration
      initialized = true
    }
  }

  return normalized
}

export function normalizeVideoClips(clips: TimelineClip[]): TimelineClip[] {
  let cumulative = 0
  const normalized = [...clips]
    .sort((a, b) => a.order - b.order)
    .map((clip, index) => {
      const startOffset = typeof clip.startOffset === 'number' ? clip.startOffset : cumulative
      cumulative += clip.effectiveDuration
      return {
        ...clip,
        order: typeof clip.order === 'number' ? clip.order : index,
        startOffset,
        trackIndex: typeof clip.trackIndex === 'number' ? Math.max(0, clip.trackIndex) : 0,
        speed: typeof clip.speed === 'number' ? clip.speed : 1.0,
        volume: typeof clip.volume === 'number' ? Math.max(0, Math.min(1, clip.volume)) : 1.0,
        muted: typeof clip.muted === 'boolean' ? clip.muted : false,
      }
    })
  return preventSameTrackOverlaps(normalized)
}

export function normalizeAudioClips(clips: TimelineAudioClip[]): TimelineAudioClip[] {
  return preventSameTrackOverlaps(
    clips.map((clip) => ({
      ...clip,
      speed: typeof clip.speed === 'number' ? clip.speed : 1.0,
      startOffset: typeof clip.startOffset === 'number' ? clip.startOffset : 0,
      trackIndex: typeof clip.trackIndex === 'number' ? Math.max(0, clip.trackIndex) : 0,
    }))
  )
}

/**
 * Build a TimelineRenderConfig from the current timeline state.
 * Shared by TimelineEditorModal (render button) and VideoEditorNode (save-on-close).
 */
export function buildTimelineRenderConfig(timelineState: TimelineState): TimelineRenderConfig {
  return {
    operation: 'timeline_render',
    timeline: {
      clips: timelineState.clips.map((clip) => ({
        id: clip.id,
        source_path: clip.sourcePath,
        start_time: clip.trimStart,
        end_time: clip.originalDuration - clip.trimEnd,
        order: clip.order,
        speed: clip.speed,
        start_offset: clip.startOffset,
        track_index: clip.trackIndex,
        volume: clip.volume,
        muted: clip.muted,
        // Persist fade-out so the backend renders the visualized fade.
        // Clamp to clip's effective duration on the way out — the worker
        // re-clamps too but a sane wire value keeps replay deterministic.
        fade_out_s: Math.max(
          0,
          Math.min(clip.effectiveDuration, clip.fadeOutS ?? 0)
        ),
        fade_in_s: Math.max(
          0,
          Math.min(clip.effectiveDuration, clip.fadeInS ?? 0)
        ),
      })),
      audio:
        timelineState.audioClips.length === 1
          ? {
              source_path: timelineState.audioClips[0].sourcePath,
              start_time: timelineState.audioClips[0].trimStart,
              end_time:
                timelineState.audioClips[0].originalDuration - timelineState.audioClips[0].trimEnd,
              volume: timelineState.audioClips[0].volume,
            }
          : null,
      audio_tracks:
        timelineState.audioClips.length > 0
          ? timelineState.audioClips
              .filter((c) => !c.muted)
              .map((c) => ({
                source_path: c.sourcePath,
                start_time: c.trimStart,
                end_time: c.originalDuration - c.trimEnd,
                volume: c.volume,
                role: c.role,
                start_offset: c.startOffset,
                speed: c.speed,
                track_index: c.trackIndex,
              }))
          : null,
      total_duration: timelineState.totalDuration,
      video_track_count: timelineState.videoTrackCount,
      audio_track_count: timelineState.audioTrackCount,
    },
  }
}

export function parseTimelineRenderConfig(
  config: TimelineRenderConfig,
  initialClips: TimelineClip[],
  initialAudioClips: TimelineAudioClip[],
): TimelineState {
  const timeline = config.timeline
  const clipsByPath = new Map(initialClips.map(c => [c.sourcePath, c]))
  const audioByPath = new Map(initialAudioClips.map(c => [c.sourcePath, c]))

  const matchedVideoPaths = new Set<string>()
  const clips: TimelineClip[] = (timeline.clips ?? [])
    .filter(wc => clipsByPath.has(wc.source_path))
    .map(wc => {
      matchedVideoPaths.add(wc.source_path)
      const base = clipsByPath.get(wc.source_path)!
      const trimStart = wc.start_time ?? 0
      const trimEnd = wc.end_time != null
        ? Math.max(0, base.originalDuration - wc.end_time)
        : 0
      const speed = wc.speed ?? 1.0
      const effectiveDuration = (base.originalDuration - trimStart - trimEnd) / speed
      return {
        ...base,
        id: wc.id ?? base.id,
        trimStart,
        trimEnd,
        effectiveDuration,
        order: wc.order ?? base.order,
        speed,
        startOffset: wc.start_offset ?? base.startOffset,
        trackIndex: wc.track_index ?? 0,
        volume: wc.volume ?? base.volume,
        muted: wc.muted ?? false,
        fadeOutS: wc.fade_out_s ?? 0,
        fadeInS: wc.fade_in_s ?? 0,
      }
    })

  for (const base of initialClips) {
    if (!matchedVideoPaths.has(base.sourcePath)) {
      const maxOrder = clips.reduce((m, c) => Math.max(m, c.order), -1)
      const end = computeTimelineDuration(clips, [])
      clips.push({ ...base, order: maxOrder + 1, startOffset: end })
    }
  }

  const matchedAudioPaths = new Set<string>()
  const audioClips: TimelineAudioClip[] = (timeline.audio_tracks ?? [])
    .filter(wa => audioByPath.has(wa.source_path))
    .map((wa, idx) => {
      matchedAudioPaths.add(wa.source_path)
      const base = audioByPath.get(wa.source_path)!
      const trimStart = wa.start_time ?? 0
      const trimEnd = wa.end_time != null
        ? Math.max(0, base.originalDuration - wa.end_time)
        : 0
      const speed = wa.speed ?? 1.0
      const effectiveDuration = (base.originalDuration - trimStart - trimEnd) / speed
      return {
        ...base,
        id: idx === 0 ? base.id : `${base.id}:cfg-${idx}`,
        trimStart,
        trimEnd,
        effectiveDuration,
        volume: wa.volume ?? base.volume,
        muted: false,
        startOffset: wa.start_offset ?? 0,
        role: wa.role ?? base.role,
        speed,
        trackIndex: wa.track_index ?? 0,
      }
    })

  for (const base of initialAudioClips) {
    if (!matchedAudioPaths.has(base.sourcePath)) {
      audioClips.push(base)
    }
  }

  const mutedVideoTracks = new Set<number>()
  for (const meta of timeline.video_tracks_meta ?? []) {
    if (meta.muted) mutedVideoTracks.add(meta.track_index)
  }
  const mutedAudioTracks = new Set<number>()
  for (const meta of timeline.audio_tracks_meta ?? []) {
    if (meta.muted) mutedAudioTracks.add(meta.track_index)
  }

  const totalDuration = timeline.total_duration ?? computeTimelineDuration(clips, audioClips)

  return {
    clips,
    audioClips,
    playheadPosition: 0,
    zoomLevel: 50,
    isPlaying: false,
    selectedClipIds: new Set<string>(),
    totalDuration,
    videoTrackCount: timeline.video_track_count ?? Math.max(1, ...clips.map(c => c.trackIndex + 1)),
    audioTrackCount: timeline.audio_track_count ?? Math.max(1, ...audioClips.map(c => c.trackIndex + 1)),
    copiedClip: null,
    copiedClipType: null,
    mutedVideoTracks,
    mutedAudioTracks,
    magnetEnabled: timeline.magnet_enabled ?? true,
  }
}
